"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { toast } from "@/lib/toast";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";
import { useWorkAreaContext } from "@/contexts/workAreaContext";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import type { WorkArea } from "@/types/workarea";
import GeneralTab from "./WorkAreaViewModal/GeneralTab";
import MembersTab from "./WorkAreaViewModal/MembersTab";
import EmojiPicker from "@/components/tailwind/editor/EmojiPicker";
import { isInsideAnchoredPopover } from "@/components/tailwind/ui/anchored-popover";

interface WorkAreaViewModalProps {
  workArea: WorkArea;
  isOpen: boolean;
  onClose: () => void;
  onMembersUpdated?: () => void;
  onWorkAreaDeleted?: () => void;
}

type TabType = "general" | "members";

const TABS: { key: TabType; label: string }[] = [
  { key: "general", label: "General" },
  { key: "members", label: "Members" },
];

export default function WorkAreaViewModal({
  workArea,
  isOpen,
  onClose,
  onMembersUpdated,
  onWorkAreaDeleted,
}: WorkAreaViewModalProps) {
  const { workspaceMembers, currentWorkspace } = useWorkspaceContext();
  const { updateWorkspaceWorkArea, deleteWorkspaceWorkArea, workAreas } = useWorkAreaContext();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("general");
  const modalRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  const [currentWorkArea, setCurrentWorkArea] = useState(workArea);

  useEffect(() => {
    if (isOpen) {
      const updatedWorkArea = workAreas.find(
        (wa) => wa._id === workArea._id || String(wa._id) === String(workArea._id),
      );
      setCurrentWorkArea(updatedWorkArea || workArea);
    }
  }, [workArea, workAreas, isOpen]);

  const isMember =
    currentWorkArea.members?.some((m) => m.userEmail === user?.email) || false;

  const workAreaOwnerMember = workspaceMembers.find(
    (m) =>
      String(m.userId) === String(currentWorkArea.ownerId) ||
      m.userId === currentWorkArea.ownerId,
  );
  const workAreaOwnerFromMembers = currentWorkArea.members?.find(
    (m) =>
      String(m.userId) === String(currentWorkArea.ownerId) ||
      m.userId === currentWorkArea.ownerId,
  );
  const finalWorkAreaOwnerMember =
    workAreaOwnerMember ||
    (workAreaOwnerFromMembers
      ? workspaceMembers.find(
          (m) =>
            m.userEmail === workAreaOwnerFromMembers.userEmail ||
            String(m.userId) === String(workAreaOwnerFromMembers.userId),
        )
      : null);

  const workAreaOwnerEmail =
    finalWorkAreaOwnerMember?.userEmail || workAreaOwnerFromMembers?.userEmail;
  const isWorkAreaOwner: boolean = !!(user?.email && workAreaOwnerEmail === user.email);

  const memberCount = currentWorkArea.members?.length || 0;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      // Tab dropdowns portal to <body> — clicks in them aren't outside.
      if (isInsideAnchoredPopover(event.target)) return;
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const refreshWorkArea = () => {
    const updated = workAreas.find(
      (wa) => wa._id === currentWorkArea._id || String(wa._id) === String(currentWorkArea._id),
    );
    if (updated) setCurrentWorkArea(updated);
  };

  // Focus + select the name input when entering edit mode.
  useEffect(() => {
    if (isEditingName) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [isEditingName]);

  const handleIconChange = async (emoji: string) => {
    if (!isWorkAreaOwner || !currentWorkArea._id) return;
    try {
      await updateWorkspaceWorkArea(currentWorkArea._id, undefined, undefined, emoji);
      setShowEmojiPicker(false);
      refreshWorkArea();
    } catch (err) {
      console.error("Failed to update icon", err);
      toast.error("Failed to update icon");
    }
  };

  const startNameEdit = () => {
    if (!isWorkAreaOwner) return;
    setDraftName(currentWorkArea.name);
    setIsEditingName(true);
  };

  const cancelNameEdit = () => {
    if (isSavingName) return;
    setIsEditingName(false);
    setDraftName("");
  };

  const commitNameEdit = async () => {
    if (isSavingName) return;
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === currentWorkArea.name) {
      setIsEditingName(false);
      setDraftName("");
      return;
    }
    setIsSavingName(true);
    try {
      await updateWorkspaceWorkArea(currentWorkArea._id, trimmed);
      refreshWorkArea();
      setIsEditingName(false);
      setDraftName("");
    } catch (err) {
      console.error("Failed to rename work area", err);
      toast.error("Failed to rename work area");
    } finally {
      setIsSavingName(false);
    }
  };

  if (!isOpen || typeof window === "undefined") return null;

  return createPortal(
    <div
      // Bottom-anchored < lg (phones + iPads in portrait), centered at lg+.
      className="fixed inset-0 z-[11000] flex items-end lg:items-center justify-center"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/30 dark:bg-black/60" onClick={onClose} />
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        // < lg: full width, 90% dvh, top corners rounded only.
        // lg+ (laptops/desktops): identical to today — 640×640 with the
        // calc() viewport caps for very tall/wide windows.
        className="relative bg-white dark:bg-zinc-900 rounded-t-lg lg:rounded-lg shadow-xl border border-component w-full lg:w-[640px] h-[90dvh] lg:h-[640px] lg:max-w-[calc(100vw-100px)] lg:max-h-[calc(100vh-80px)] flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              {/* Square rounded icon (click to change) */}
              <div
                ref={iconRef}
                role={isWorkAreaOwner ? "button" : undefined}
                tabIndex={isWorkAreaOwner ? 0 : -1}
                onClick={() => {
                  if (isWorkAreaOwner) setShowEmojiPicker(true);
                }}
                onKeyDown={(e) => {
                  if (!isWorkAreaOwner) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowEmojiPicker(true);
                  }
                }}
                className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 flex items-center justify-center overflow-hidden",
                  isWorkAreaOwner &&
                    "cursor-pointer hover:opacity-80 transition-opacity",
                )}
                title={isWorkAreaOwner ? "Change icon" : undefined}
              >
                {currentWorkArea.icon ? (
                  <span className="flex items-center justify-center">
                    {renderIcon(currentWorkArea.icon, { style: { width: 28, height: 28 } })}
                  </span>
                ) : (
                  <span className="text-base font-medium text-zinc-600 dark:text-zinc-300">
                    {currentWorkArea.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="min-w-0 flex-1 pt-0.5">
                {isEditingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void commitNameEdit();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelNameEdit();
                      }
                    }}
                    onBlur={() => void commitNameEdit()}
                    disabled={isSavingName}
                    maxLength={120}
                    className="w-full text-lg font-semibold text-zinc-900 dark:text-zinc-100 bg-transparent border-b-2 border-blue-500 dark:border-blue-500 outline-none disabled:opacity-50"
                  />
                ) : (
                  <h2
                    onClick={startNameEdit}
                    title={isWorkAreaOwner ? "Click to rename" : currentWorkArea.name}
                    className={cn(
                      "text-lg font-semibold text-zinc-900 dark:text-zinc-100 truncate",
                      isWorkAreaOwner &&
                        "cursor-text rounded px-1 -mx-1 hover-component  transition-colors",
                    )}
                  >
                    {currentWorkArea.name}
                  </h2>
                )}
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {memberCount} {memberCount === 1 ? "member" : "members"}
                </div>
              </div>
            </div>

            {isMember && (
              <div className="flex-shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium text-zinc-700 dark:text-zinc-200 border border-component bg-white dark:bg-zinc-800">
                <Check className="w-3.5 h-3.5" />
                Joined
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 px-6 border-b border-component">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              tabIndex={activeTab === tab.key ? 0 : -1}
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "relative py-2 text-sm font-medium transition-colors",
                activeTab === tab.key
                  ? "text-zinc-900 dark:text-zinc-100"
                  : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100",
              )}
            >
              {tab.label}
              {activeTab === tab.key && (
                <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-zinc-900 dark:bg-zinc-100" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === "general" && (
            <GeneralTab
              workArea={currentWorkArea}
              ownerMember={finalWorkAreaOwnerMember || undefined}
              isOwner={isWorkAreaOwner}
              isMember={isMember}
              workspaceName={currentWorkspace?.name || "this workspace"}
              memberAccessLocked={currentWorkspace?.type === "public"}
              onDescriptionChange={async (description) => {
                await updateWorkspaceWorkArea(
                  currentWorkArea._id,
                  undefined,
                  description,
                  undefined,
                  undefined,
                );
                refreshWorkArea();
              }}
              onAccessLevelChange={async (newAccessLevel) => {
                await updateWorkspaceWorkArea(
                  currentWorkArea._id,
                  undefined,
                  undefined,
                  undefined,
                  newAccessLevel,
                );
                refreshWorkArea();
              }}
              onMemberAccessChange={async (newMemberAccess) => {
                await updateWorkspaceWorkArea(
                  currentWorkArea._id,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  newMemberAccess,
                );
                refreshWorkArea();
              }}
              onLeave={async () => {
                onClose();
                onMembersUpdated?.();
              }}
              onArchive={async () => {
                await deleteWorkspaceWorkArea(currentWorkArea._id);
                onClose();
                onWorkAreaDeleted?.();
              }}
            />
          )}
          {activeTab === "members" && (
            <MembersTab
              workArea={currentWorkArea}
              workspaceMembers={workspaceMembers}
              workspaceOwnerId={currentWorkspace?.ownerId}
              isOwner={isWorkAreaOwner}
              onMembersUpdated={() => {
                refreshWorkArea();
                onMembersUpdated?.();
              }}
            />
          )}
        </div>

        {/* Emoji picker — nested inside modalRef so the modal's
            outside-click handler doesn't fire when clicking the picker. */}
        {showEmojiPicker && isWorkAreaOwner && (
          <div
            className="fixed inset-0 z-[12000]"
            onClick={(e) => {
              e.stopPropagation();
              setShowEmojiPicker(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <EmojiPicker
              anchorRef={iconRef}
              currentEmoji={currentWorkArea.icon || "🏢"}
              onSelect={handleIconChange}
              onClose={() => setShowEmojiPicker(false)}
              onRemove={() => handleIconChange("")}
              position="fixed"
            />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
